import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useProbationReviewsApi from './config';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import { useAuth } from '../../context/AuthContext';

const Container = styled.div``;

/**
 * Deneme süresi değerlendirmeleri (spec §11 `probation.reviewOverdue` kuralının veri girişi).
 *
 * PbDocumentsPage'in deseni birebir izlenir: sunucu taraflı DataTable + OptionsMenu satır
 * aksiyonları + GenericFormDrawer. `data[tableMeta.rowIndex]` burada GÜVENLİDİR çünkü DataTable
 * varsayılan olarak `serverSide` çalışır (sıralama sunucuya gider, istemci kopyayı yeniden
 * sıralamaz) — HrAnalyticsAlertsPage'te bu güvenli DEĞİLDİ, orası `serverSide={false}` kullanıyor
 * ve satır çözümü `currentTableData[rowIndex].index` üzerinden yapılmak zorunda.
 */
export default function ProbationReviewsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('EM') : false;

  const [queryParams, setQueryParams] = useState({ pageSize: 25, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [confirm, setConfirm] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  // { id, employeeFullName } — karar verilecek satır
  const [completing, setCompleting] = useState(null);

  const {
    fetching, data, pagination, columns: baseColumns,
    createSchema, completeSchema, create, complete, cancel,
  } = useProbationReviewsApi(queryParams);

  const handleDataStateChange = useCallback((act, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  const handleCreate = useCallback(async (formData) => {
    const ok = await create(formData);
    if (ok) setCreateOpen(false);
    return ok;
  }, [create]);

  const handleComplete = useCallback(async (formData) => {
    const ok = await complete(completing.id, formData);
    if (ok) setCompleting(null);
    return ok;
  }, [complete, completing]);

  const buildActions = useCallback((row) => {
    if (!row || !writable) return [];
    // Karar ve iptal YALNIZ bekleyen satırda anlamlı — tamamlanmış bir kayıt append-only
    // geçmiştir, üzerine yazılmaz (bkz. ProbationReview model yorumu).
    if (row.status !== 'pending') return [];
    return [
      {
        type: 'edit',
        label: t('probationReview.action.complete'),
        onClick: () => setCompleting({ id: row.id, employeeFullName: row.employeeFullName }),
      },
      {
        type: 'delete',
        label: t('probationReview.action.cancel'),
        onClick: () => setConfirm({
          message: t('probationReview.confirm.cancel', { name: row.employeeFullName }),
          onYes: () => cancel(row.id),
        }),
      },
    ];
  }, [t, writable, cancel]);

  const columns = useMemo(() => {
    if (!writable) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const actions = buildActions(row);
            return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
          },
        },
      },
    ];
  }, [baseColumns, data, buildActions, writable]);

  return (
    <Container>
      <GenericFormDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t('probationReview.new')}
        schema={createSchema}
        initialValues={null}
        onSave={handleCreate}
      />

      <GenericFormDrawer
        open={!!completing}
        onClose={() => setCompleting(null)}
        title={t('probationReview.completeTitle', { name: completing?.employeeFullName || '' })}
        schema={completeSchema}
        initialValues={null}
        onSave={handleComplete}
      />

      <DataTable
        title={t('probationReview.pageTitle')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        canCreateNewRecord={writable}
        onNewButtonClick={() => setCreateOpen(true)}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
      />

      <ConfirmDialog
        isOpen={!!confirm}
        message={confirm?.message}
        onConfirm={async () => { await confirm.onYes(); setConfirm(null); }}
        onCancel={() => setConfirm(null)}
      />
    </Container>
  );
}
